import { Link } from 'react-router-dom';
import { Sparkles, ArrowRight, Star } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Navbar } from '@/components/Navbar';
import { ConstellationCanvas } from '@/components/ConstellationCanvas';
import { StoryPreviewCard } from '@/components/StoryPreviewCard';
import { MythologyGrid } from '@/components/MythologyGrid';
import { PricingCards } from '@/components/PricingCards';
import { usePricing } from '@/contexts/PricingContext';

const SEO_KEYWORDS = [
  'personalized mythology stories for kids',
  "Hindu mythology for kids",
  'Greek mythology bedtime story',
  'Norse mythology for children',
  'AI bedtime story generator',
  'personalized bedtime stories',
  "mythology story with child's name",
  'NRI Indian stories for kids',
  'sibling bedtime story generator',
  'Ganesha story for kids',
];

export default function LandingPage() {
  const { loading, pricing } = usePricing();
  const singleDisplay = loading ? '…' : pricing.single.display;

  return (
    <div className="min-h-screen bg-white">
      {/* HERO (dark) */}
      <section data-testid="landing-hero" className="relative overflow-hidden bg-tl-hero text-white">
        <ConstellationCanvas />
        <div className="absolute inset-0 tl-hero-vignette" aria-hidden="true" />
        <div className="relative z-10">
          <Navbar variant="dark" />

          <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 pt-6 pb-20 sm:pt-8 sm:pb-24 lg:pb-28">
            {/* Pill badge */}
            <div className="flex justify-center">
              <div className="inline-flex items-center gap-2 rounded-full border border-tl-purple/60 bg-tl-purple/25 px-3 py-1.5">
                <Sparkles className="h-3.5 w-3.5 text-tl-gold" />
                <span className="text-[11px] text-tl-textSecondaryOnDark">AI-powered mythology story generator for kids</span>
              </div>
            </div>

            {/* H1 */}
            <h1 data-testid="landing-hero-h1" className="mt-6 text-center text-white font-semibold leading-[1.2] max-w-[560px] mx-auto text-[24px] sm:text-[28px] lg:text-[32px]">
              What happens when your child hears their name in tonight's{' '}
              <span className="text-tl-gold">mythology</span>{' '}
              bedtime story?
            </h1>

            {/* Subtitle */}
            <p className="mt-4 text-center text-tl-textSecondaryOnDark text-[13px] sm:text-[14px] leading-relaxed max-w-[460px] mx-auto">
              Personalized mythology stories for kids — 12 traditions, 40+ languages, generated in 30 seconds. Your child as the hero. Every night, a new adventure.
            </p>

            {/* CTAs */}
            <div className="mt-6 flex flex-col sm:flex-row items-center justify-center gap-3">
              <Link to="/create" data-testid="landing-primary-cta-button" className="w-full sm:w-auto">
                <Button className="w-full sm:w-auto h-12 px-5 bg-tl-purple hover:bg-tl-purpleDark text-white text-[13px] gap-2 animate-tl-cta-pulse shadow-tl-card">
                  <Sparkles className="h-4 w-4" />
                  Create your child's free story
                </Button>
              </Link>
              <a href="#sample" data-testid="landing-sample-cta-button" className="w-full sm:w-auto">
                <Button variant="outline" className="w-full sm:w-auto h-12 px-5 bg-transparent border-tl-border text-tl-textSecondaryOnDark hover:bg-white/5 text-[13px]">
                  See a sample story
                </Button>
              </a>
            </div>

            {/* Social proof */}
            <div className="mt-5 text-center text-[11px] text-tl-textSecondaryOnDark">
              <span className="text-tl-gold">★★★★★</span>{' '}
              4,800 families · 12 world mythologies · stories in Hindi, English, Gujarati + 37 more languages
            </div>

            {/* Sample preview */}
            <div id="sample">
              <StoryPreviewCard />
            </div>
          </div>
        </div>

        {/* Section separator */}
        <div className="relative h-6 bg-tl-dark2" aria-hidden="true" />
      </section>

      {/* HOW IT WORKS (light) */}
      <section id="how" data-testid="landing-how-it-works" className="bg-white py-14 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.14em] text-tl-purple">How the AI mythology story generator works</div>
            <h2 className="mt-2 text-[22px] sm:text-[26px] font-semibold text-slate-900 max-w-2xl mx-auto">
              Create a personalized mythology bedtime story in 3 steps
            </h2>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-5">
            {[
              { n: 1, title: 'Fill the form', text: "Your child's name, age, 2 things they love, and mythology tradition. 60 seconds." },
              { n: 2, title: 'AI writes it', text: 'Our mythology story generator creates a unique 500-word personalized bedtime story in 8 seconds.' },
              { n: 3, title: 'Read and share', text: 'Download a beautiful PDF. Share on WhatsApp. Your child asks to hear it again tomorrow.' },
            ].map((s) => (
              <div key={s.n} className="bg-white border border-slate-200 rounded-tlLg p-6 shadow-tl-soft">
                <div className="w-9 h-9 rounded-full bg-tl-purple text-white text-[14px] font-semibold flex items-center justify-center">{s.n}</div>
                <div className="mt-3 text-[16px] font-semibold text-slate-900">{s.title}</div>
                <p className="mt-1 text-[13px] leading-6 text-slate-600">{s.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MYTHOLOGIES (light) */}
      <section id="mythologies" className="bg-slate-50/60 py-14 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.14em] text-tl-purple">12 world mythology traditions for kids</div>
            <h2 className="mt-2 text-[22px] sm:text-[26px] font-semibold text-slate-900 max-w-3xl mx-auto">
              Hindu mythology for kids · Greek mythology · Norse mythology · and 9 more
            </h2>
          </div>

          <div className="mt-10">
            <MythologyGrid />
          </div>
        </div>
      </section>

      {/* SIBLING STORIES (light) */}
      <section data-testid="landing-sibling-section" className="bg-white py-14 sm:py-16 lg:py-20">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 grid grid-cols-1 md:grid-cols-2 gap-8 items-center">
          <div>
            <div className="text-[10px] uppercase tracking-[0.14em] text-tl-purple">Sibling bedtime stories — both heroes</div>
            <h2 className="mt-2 text-[22px] sm:text-[26px] font-semibold text-slate-900">
              Personalized bedtime stories for siblings — one adventure, two heroes
            </h2>
            <p className="mt-3 text-[13px] leading-6 text-slate-600 max-w-md">
              The only mythology story generator that puts two siblings in the same adventure. Each child's unique strengths drive the plot. No competitor does this.
            </p>
            <div className="mt-5 flex gap-2 text-[12px] text-slate-500">
              <div className="inline-flex items-center gap-1 bg-tl-purpleLight text-tl-purple px-2 py-1 rounded-full"><Star className="h-3 w-3" /> Both children essential</div>
              <div className="inline-flex items-center gap-1 bg-[#F6E7D3] text-tl-interest px-2 py-1 rounded-full">One story</div>
            </div>
          </div>

          <div className="bg-tl-purpleLight/60 border border-tl-purple/30 rounded-tlLg p-6 font-story text-[13px] leading-7 text-slate-800">
            <div className="text-[10px] uppercase tracking-[0.14em] text-tl-purple mb-2">Sample sibling story</div>
            <p>
              “<span className="text-tl-purple font-medium">Priya</span> raised her paintbrush and colored a bright bridge across the river. Behind her, <span className="text-tl-purple font-medium">Arjun</span> hummed his football chant and the wooden planks locked into place. <span className="text-tl-success font-medium">Athena</span> smiled—neither alone could have crossed, but together, they had built a path only they could imagine.”
            </p>
          </div>
        </div>
      </section>

      {/* PRICING (light) */}
      <section id="pricing" data-testid="landing-pricing" className="bg-slate-50/60 py-14 sm:py-16 lg:py-20">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <div className="text-[10px] uppercase tracking-[0.14em] text-tl-purple">Global pricing · families worldwide</div>
            <h2 className="mt-2 text-[22px] sm:text-[26px] font-semibold text-slate-900">
              Personalized mythology stories — from <span className="text-tl-purple">{singleDisplay}</span>
            </h2>
            <p className="mt-2 text-[13px] text-slate-500">Country-aware pricing. You see prices in your currency.</p>
          </div>
          <div className="mt-10">
            <PricingCards />
          </div>
        </div>
      </section>

      {/* SEO Footer strip (dark) */}
      <footer data-testid="landing-footer" className="bg-tl-hero text-white py-10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex items-center gap-2 text-white">
            <Sparkles className="h-4 w-4 text-tl-gold" />
            <span className="font-medium text-[14px]">TalesLore</span>
          </div>
          <p className="mt-3 text-[12px] text-tl-textSecondaryOnDark max-w-3xl">
            A bedtime storytelling platform where your child is the hero of ancient mythology, generated fresh every night.
          </p>
          <div className="tl-seo-strip mt-5 text-tl-textSecondaryOnDark">
            {SEO_KEYWORDS.join(' · ')}
          </div>
          <div className="mt-6 flex flex-wrap items-center gap-4 text-[11px] text-tl-textSecondaryOnDark">
            <Link to="/create" className="inline-flex items-center gap-1 hover:text-white">Create free story <ArrowRight className="h-3 w-3" /></Link>
            <span>© {new Date().getFullYear()} TalesLore</span>
          </div>
        </div>
      </footer>
    </div>
  );
}
